import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildMarketingEmailSettingsPayloadFromForm,
  mapMarketingEmailSettingsResponseToDto,
  validateMarketingEmailSettingsPayload,
} from '../dto/marketing-email-providers.dto.js';

const DEFAULT_FORM = {
  defaultBatchSize: 100,
  unsubscribeFooterText: 'Unsubscribe: {{unsubscribeUrl}}',
};

export function useMarketingEmailSettings() {
  const { token } = useAuth();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.getMarketingEmailSettings(token);
      setForm(mapMarketingEmailSettingsResponseToDto(response));
    } catch (requestError) {
      setForm(DEFAULT_FORM);
      setError(requestError.message || 'Failed to load marketing email settings');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const saveSettings = useCallback(async (event) => {
    event.preventDefault();
    const payload = buildMarketingEmailSettingsPayloadFromForm(form);
    const nextErrors = validateMarketingEmailSettingsPayload(payload);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.updateMarketingEmailSettings(token, payload);
      setForm(mapMarketingEmailSettingsResponseToDto(response));
      setSuccess('Marketing email settings saved.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to save marketing email settings');
    } finally {
      setIsSaving(false);
    }
  }, [form, token]);

  return {
    form,
    isLoading,
    isSaving,
    error,
    success,
    formErrors,
    onFormChange,
    saveSettings,
    reload: loadSettings,
  };
}
